import { useLayoutEffect, useRef } from 'react';

// Long enough to be followed across the page, eased out of its old place
// and into the new one. Matches the weight of --dur-turn.
const DURATION = 620;
const EASE = 'cubic-bezier(0.45, 0, 0.2, 1)';

/**
 * Carries an element from where it was to where it now is, each time `key`
 * changes.
 *
 * The element itself is never unmounted — only its place in the column
 * changes — so this animation is the whole of the move the reader sees. It
 * is the same trick as useCardFlight: the browser has already laid the
 * element out in its new place, and this puts it back where it was and lets
 * it travel home.
 *
 * Positions are kept in document coordinates. The page scrolls between the
 * two measurements (useFlowScroll glides to each new step), and a viewport
 * rect would read that scroll as part of the journey.
 */
export function useFlipOnMove(ref, key) {
  const last = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const box = el.getBoundingClientRect();
    const next = { x: box.left + window.scrollX, y: box.top + window.scrollY };
    const previous = last.current;
    last.current = next;

    // Nothing to travel from on the first paint
    if (!previous || !el.animate) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const dx = previous.x - next.x;
    const dy = previous.y - next.y;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

    el.animate(
      { translate: [`${dx}px ${dy}px`, '0px 0px'] },
      { duration: DURATION, easing: EASE }
    );
  }, [key, ref]);
}

export default useFlipOnMove;
